import { makeAutoObservable, runInAction } from "mobx";
import { db } from "@stores/environments/Database.web";
import { ref, get, set, push, child } from "firebase/database";
import { IPost } from "@interfaces/ipost";
import { logService } from "../services/LogService";
import { authStore } from "./AuthStore";
class PostStore {
  posts: IPost[] = [];
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  fetchPosts = async () => {
    this.loading = true;
    const dbRef = ref(db);

    try {
      const snapshot = await get(child(dbRef, "blog"));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const allPosts: IPost[] = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        runInAction(() => {
          this.posts = allPosts
            .filter(post => !post.deleted)
            .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.posts = [];
          this.loading = false;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar posts:", error);
      runInAction(() => {
        this.error = "Erro ao carregar posts.";
        this.loading = false;
      });
    }
  };

  savePost = async (post: Omit<IPost, "id">, id?: string) => {
    const postId = id || push(ref(db, "blog")).key;
    const dbRef = ref(db, `blog/${postId}`);
    
    // Verifica se é uma criação ou atualização
    const isUpdate = !!id;
    
    // Busca o valor anterior se for uma atualização
    let oldValue = null;
    if (isUpdate) {
      try {
        const snapshot = await get(dbRef);
        oldValue = snapshot.exists() ? snapshot.val() : null;
      } catch (error) {
        console.error('Erro ao buscar valor anterior:', error);
      }
    }
    
    await set(dbRef, { ...post });
    
    // Registra o log
    if (authStore._user) {
      const logData = isUpdate 
        ? logService.updateLogData(
            authStore._user.uid,
            authStore._user.email || '',
            'Post',
            postId,
            `blog/${postId}`,
            oldValue,
            post,
            `Post ${isUpdate ? 'atualizado' : 'criado'}: ${post.title}`
          )
        : logService.createLogData(
            authStore._user.uid,
            authStore._user.email || '',
            'Post',
            postId,
            `blog/${postId}`,
            post,
            `Post criado: ${post.title}`
          );
      
      await logService.logOperation(logData);
    }
    
    this.fetchPosts();
  };

  deletePost = async (id: string) => {
    const confirmed = window.confirm("Tem certeza que deseja excluir este post?");
    if (!confirmed) return;

    // Busca o valor anterior para o log
    const dbRef = ref(db, `blog/${id}`);
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }

    const deleteRef = ref(db, `blog/${id}/deleted`);
    await set(deleteRef, true);
    
    // Registra o log
    if (authStore._user && oldValue) {
      const logData = logService.deleteLogData(
        authStore._user.uid,
        authStore._user.email || '',
        'Post',
        id,
        `blog/${id}`,
        oldValue,
        `Post excluído: ${oldValue.title}`
      );
      
      await logService.logOperation(logData);
    }
    
    this.fetchPosts();
  };
}

export const postStore = new PostStore();
